import * as React from "react";
import { observer } from "mobx-react-lite";
import ShiftThemeContainer from "../../shift/ShiftThemeContainer";
import { useWeekdaySelection, useKeyboardNavigation, useHoverFocus } from "./hooks";
import ISmartScheduleShiftEntity, { WeekDays } from "sh-models/shift/ISmartScheduleShiftEntity";
import { ScheduleCellRenderSize } from "../../../../StaffHubConstants";
import Shift from "../../shift/Shift";
import { SlotSelector } from "../../shift/editorContentShift/SlotSelector";
import {
    Checkbox,
    CheckboxOnChangeData,
    Fluent9Text as Text,
    Button,
    DeleteRegular,
    EditRegular,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverSurface,
    TeachingPopoverTitle
} from "@microsoft/shifts-ui-core";
import {
    Tooltip,
    TeachingPopoverCarousel,
    TeachingPopoverCarouselCard,
    TeachingPopoverCarouselFooter,
    TeachingPopoverCarouselNav,
    TeachingPopoverCarouselNavButton
} from "@fluentui/react-components";
import {
    IDropdownOption,
    IToggle,
    } from "@fluentui/react";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import {
    MINIMUM_OPEN_SHIFT_SLOTS
    } from "sh-application/../StaffHubConstants";
import StringsStore from "../../../../sh-strings/store";
import { scheduleGenerationEditorState } from "../store/store";
import { deleteShiftBlock, updateSlotsSmartScheduleShiftById } from "../../smartSchedule/mutatorActions/actions";
import { launchShiftEditor, shiftEditorViewState } from "../../shift/lib";
import { ScheduleCalendarTypes, ShiftTypes } from "../../../../sh-models";
import { useStyles } from "./ShiftBlock.styles";
import { getStrings } from "@microsoft/shifts-experiences-shared";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "../../../../sh-services";
import { hasSeenFRE, setFREsShownSoFar } from "sh-stores";
import { UserFRETypes } from "sh-models";
import type { TeachingPopoverCarouselFooterButtonProps } from "@fluentui/react-teaching-popover";
import type { OnOpenChangeData, OpenPopoverEvents } from "@fluentui/react-popover";
const classNames = require("classnames/bind");

const memoize = UNIT_TEST_MODE ? require('memoize-one') : require('memoize-one').default;

export const MAXIMUM_SMART_SCHEDULE_SHIFT_SLOTS = 80;

interface IProps {
    shift: ISmartScheduleShiftEntity;
    onDeleted?: () => void;
    showTeachingPopover?: boolean;
    onTeachingPopoverDismiss?: () => void;
}

enum ShiftBlockTeachingStep {
    EditOrDeleteShifts = "editOrDeleteShiftsStep",
    ShiftRecurrence = "shiftRecurrenceStep"
}

const ShiftBlock: React.FC<IProps> = (props: IProps) => {
    const _strings = StringsStore().registeredStringModules.get("addEditShiftTimeOffPanelFullPage").strings;
    const _weekStrings = StringsStore().registeredStringModules.get("dayOfWeek").strings;
    const _smartScheduleStrings = StringsStore().registeredStringModules.get("smartSchedule").strings;

    const WEEKDAYS_MAP = new Map<string, WeekDays>([
        [_weekStrings.get("sunday"), WeekDays.Sunday],
        [_weekStrings.get("monday"), WeekDays.Monday],
        [_weekStrings.get("tuesday"), WeekDays.Tuesday],
        [_weekStrings.get("wednesday"), WeekDays.Wednesday],
        [_weekStrings.get("thursday"), WeekDays.Thursday],
        [_weekStrings.get("friday"), WeekDays.Friday],
        [_weekStrings.get("saturday"), WeekDays.Saturday]
    ]);

    // Map for full day names used in aria-labels for screen reader accessibility
    const WEEKDAYS_FULL_NAME_MAP = new Map<WeekDays, string>([
        [WeekDays.Sunday, _weekStrings.get("sundayFull")],
        [WeekDays.Monday, _weekStrings.get("mondayFull")],
        [WeekDays.Tuesday, _weekStrings.get("tuesdayFull")],
        [WeekDays.Wednesday, _weekStrings.get("wednesdayFull")],
        [WeekDays.Thursday, _weekStrings.get("thursdayFull")],
        [WeekDays.Friday, _weekStrings.get("fridayFull")],
        [WeekDays.Saturday, _weekStrings.get("saturdayFull")]
    ]);
    const { shift } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const editButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const repeatEverydayCheckboxRef = React.useRef<HTMLDivElement | null>(null);
    const weekdayRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const styles = useStyles();

    // Use weekday selection hook
    const {
        selectedDays,
        showWeekdays,
        toggleDay,
        handleRepeatEverydayChange
    } = useWeekdaySelection({ shift });

    // Use keyboard navigation hook
    const {
        isInteractiveMode,
        setIsInteractiveMode,
        focusedDayIndex,
        handleContainerKeyDown,
        handleContainerClick,
        handleWeekdayKeyDown
    } = useKeyboardNavigation({
        weekdaysCount: WEEKDAYS_MAP.size,
        containerRef,
        weekdayRefs,
        showWeekdays,
        onToggleDay: toggleDay
    });

    // Use hover/focus hook for button visibility
    const {
        showIconButtons,
        handleMouseEnter,
        handleMouseLeave,
        handleFocus: handleContainerFocus,
        handleBlur: handleHoverBlur
    } = useHoverFocus();

    const shouldShowIconButtons = showIconButtons || !!props.showTeachingPopover;

    const [isTeachingPopoverReady, setIsTeachingPopoverReady] = React.useState(false);

    const [shiftBlocksTeachingStep, setShiftBlocksTeachingStep] = React.useState<ShiftBlockTeachingStep>(ShiftBlockTeachingStep.EditOrDeleteShifts);
    const hasRecordedEditOrDeleteRef = React.useRef<boolean>(hasSeenFRE(UserFRETypes.EditOrDeleteShifts));
    const hasRecordedShiftRecurrenceRef = React.useRef<boolean>(hasSeenFRE(UserFRETypes.ShiftRecurrence));

    React.useEffect(() => {
        if (props.showTeachingPopover) {
            setIsTeachingPopoverReady(true);
        } else {
            setIsTeachingPopoverReady(false);
        }
    }, [props.showTeachingPopover]);

    const handleShiftBlocksTeachingPopoverOpenChange = React.useCallback((_: OpenPopoverEvents, data: OnOpenChangeData) => {
        if (!data.open) {
            setShiftBlocksTeachingStep(ShiftBlockTeachingStep.EditOrDeleteShifts);
            props.onTeachingPopoverDismiss?.();
        }
    }, [props.onTeachingPopoverDismiss]);

    // Handle blur - exit interactive mode when focus leaves this ShiftBlock
    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        handleHoverBlur(e);

        // Check if the new focus target is outside the ShiftBlock container
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
            setIsInteractiveMode(false);
        }
    }, [handleHoverBlur, setIsInteractiveMode]);

    // Force re-render when specific properties change that we care about
    React.useEffect(() => {
        // This effect will trigger whenever shift or its important properties change
        // The component will re-render as a result
    }, [shift, shift.openSlots, shift.title, shift.startTime, shift.endTime]);

    const getSlotDropdownOptions = memoize((minSlots: number, maxSlots: number): IDropdownOption[] => {
        let items: IDropdownOption[] = [];
        for (let i = minSlots; i <= maxSlots; i++) {
            const title = i === 1 ? _strings.get("slotsOptionLabelSingular").format(String(i)) : _strings.get("slotsOptionLabelPlural").format(String(i));
            items.push({
                key: i,
                text: title,
                ariaLabel: title
            });
        }

        return items;
    });

    const options = getSlotDropdownOptions(MINIMUM_OPEN_SHIFT_SLOTS, ECSConfigService.getECSFeatureSetting(ECSConfigKey.SmartSchedulingMaxSlotLimit) || MAXIMUM_SMART_SCHEDULE_SHIFT_SLOTS);

    const _onSmartScheduleSlotChange = (selectedValue: number) => {
        updateSlotsSmartScheduleShiftById(selectedValue, shift.id);
    };

    const onEditShiftBlock = (shiftId: string) => {
        let editShift = scheduleGenerationEditorState().shiftBlocks.find(shift => shift.id === shiftId);
        launchShiftEditor(
            { smartScheduleShift: editShift },
            ShiftTypes.SmartSchedule,
            null, // member
            scheduleGenerationEditorState().selectedGroupId || "", // groupTagId
            ScheduleCalendarTypes.Day,
            true, // isEditShift
            false, // isEditTimeoff
            shiftEditorViewState(),
            undefined, // startDate
            false, // isOpenShift
            true, // isSmartScheduleShift
            null // elementToFocusOnDismiss
        );
    };

    const onChangeRepeatEveryDay = (_: React.FormEvent<IToggle>, data: CheckboxOnChangeData) => {
        handleRepeatEverydayChange(!!data.checked);
    };

    const handleWeekdayClick = (day: WeekDays, index: number) => {
        toggleDay(day);
    };

    // Create aria-label for the shift block container
    const shiftAriaLabel = React.useMemo(() => {
        const shiftLabel = ShiftUtils.calculateShiftTitleAriaLabelForShiftBlock(shift);
        return `${getStrings("smartSchedule", "shiftBlock")} - ${shiftLabel}`;
    }, [shift]);

    const moveToShiftRecurrenceStep = React.useCallback(() => {
        if (shiftBlocksTeachingStep === ShiftBlockTeachingStep.ShiftRecurrence) {
            return;
        }

        // Next from Edit/Delete Shifts to Shift Recurrence
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreShiftBlockEditOrDeleteNextClicked);

        setShiftBlocksTeachingStep(ShiftBlockTeachingStep.ShiftRecurrence);
    }, [shiftBlocksTeachingStep]);

    const moveToEditOrDeleteShiftsStep = React.useCallback(() => {
        if (shiftBlocksTeachingStep === ShiftBlockTeachingStep.EditOrDeleteShifts) {
            return;
        }

        // Back from Shift Recurrence to Edit/Delete Shifts
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreShiftBlockRecurrenceBackClicked);

        setShiftBlocksTeachingStep(ShiftBlockTeachingStep.EditOrDeleteShifts);
    }, [shiftBlocksTeachingStep]);

    const completeShiftBlocksTeachingSteps = React.useCallback(() => {
        // Got it on Shift Recurrence step
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreShiftBlockRecurrenceGotItClicked);

        props.onTeachingPopoverDismiss?.();
    }, [props.onTeachingPopoverDismiss]);

    React.useEffect(() => {
        if (!props.showTeachingPopover || !isTeachingPopoverReady) {
            return;
        }

        const recordFREForCurrentStep = async () => {
            try {
                if (shiftBlocksTeachingStep === ShiftBlockTeachingStep.EditOrDeleteShifts && !hasRecordedEditOrDeleteRef.current) {
                    await setFREsShownSoFar([UserFRETypes.EditOrDeleteShifts]);
                    hasRecordedEditOrDeleteRef.current = true;
                } else if (shiftBlocksTeachingStep === ShiftBlockTeachingStep.ShiftRecurrence && !hasRecordedShiftRecurrenceRef.current) {
                    await setFREsShownSoFar([UserFRETypes.ShiftRecurrence]);
                    hasRecordedShiftRecurrenceRef.current = true;
                }
            } catch {
                // Swallow telemetry persistence errors
            }
        };

        recordFREForCurrentStep();
    }, [shiftBlocksTeachingStep, isTeachingPopoverReady, props.showTeachingPopover]);

    const handleShiftBlocksTeachingCarouselValueChange = React.useCallback((_: unknown, data: { value?: string }) => {
        if (!data.value) {
            return;
        }

        switch (data.value) {
            case ShiftBlockTeachingStep.EditOrDeleteShifts:
                moveToEditOrDeleteShiftsStep();
                break;
            case ShiftBlockTeachingStep.ShiftRecurrence:
                moveToShiftRecurrenceStep();
                break;
            default:
                break;
        }
    }, [moveToEditOrDeleteShiftsStep, moveToShiftRecurrenceStep]);

    const renderShiftBlocksTeachingPopover = (): React.ReactNode => {
        if (!props.showTeachingPopover || !isTeachingPopoverReady) {
            return null;
        }

        const isEditOrDeleteStep = shiftBlocksTeachingStep === ShiftBlockTeachingStep.EditOrDeleteShifts;

        const previousButtonSlot: TeachingPopoverCarouselFooterButtonProps | null = isEditOrDeleteStep
            ? null
            : {
                navType: "prev",
                altText: getStrings("teamSetupPicker", "backButtonLabel"),
                as: "button",
                onClick: () => moveToEditOrDeleteShiftsStep(),
                children: getStrings("teamSetupPicker", "backButtonLabel")
            };

        const nextButtonSlot: TeachingPopoverCarouselFooterButtonProps = isEditOrDeleteStep
            ? {
                navType: "next",
                altText: getStrings("common", "next"),
                as: "button",
                onClick: () => {
                    moveToShiftRecurrenceStep();
                },
                children: getStrings("common", "next")
            }
            : {
                navType: "next",
                altText: getStrings("common", "gotIt"),
                as: "button",
                onClick: () => {
                    completeShiftBlocksTeachingSteps();
                },
                children: getStrings("common", "gotIt")
            };

        return (
            <TeachingPopover
                open={props.showTeachingPopover}
                trapFocus={false}
                positioning={{ position: "above", align: "start", target: shiftBlocksTeachingStep === ShiftBlockTeachingStep.ShiftRecurrence ? repeatEverydayCheckboxRef.current : editButtonRef.current, offset: { crossAxis: 0, mainAxis: 14 } }}
                onOpenChange={handleShiftBlocksTeachingPopoverOpenChange}
            >
                <TeachingPopoverSurface style={{ width: 320 }}>
                    <TeachingPopoverCarousel
                        value={shiftBlocksTeachingStep}
                        onValueChange={handleShiftBlocksTeachingCarouselValueChange}
                    >
                        <TeachingPopoverCarouselCard value={ShiftBlockTeachingStep.EditOrDeleteShifts}>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>{getStrings("smartSchedule", "teachingBubbleEditOrDeleteShiftsTitle")}</TeachingPopoverTitle>
                                <span>
                                    {getStrings("smartSchedule", "teachingBubbleEditOrDeleteShiftsContent")}
                                </span>
                            </TeachingPopoverBody>
                        </TeachingPopoverCarouselCard>
                        <TeachingPopoverCarouselCard value={ShiftBlockTeachingStep.ShiftRecurrence}>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>{getStrings("smartSchedule", "teachingBubbleShiftRecurrenceTitle")}</TeachingPopoverTitle>
                                <span>
                                    {getStrings("smartSchedule", "teachingBubbleShiftRecurrenceContent")}
                                </span>
                            </TeachingPopoverBody>
                        </TeachingPopoverCarouselCard>
                        <TeachingPopoverCarouselFooter
                            previous={previousButtonSlot as any}
                            next={nextButtonSlot as any}
                            initialStepText={""}
                            finalStepText={getStrings("common", "gotIt")}
                        >
                            <TeachingPopoverCarouselNav>
                                {(value: string) => (
                                    <TeachingPopoverCarouselNavButton
                                        aria-label={value === ShiftBlockTeachingStep.ShiftRecurrence
                                            ? getStrings("smartSchedule", "teachingBubbleShiftRecurrenceTitle")
                                            : getStrings("smartSchedule", "teachingBubbleEditOrDeleteShiftsTitle")}
                                    />
                                )}
                            </TeachingPopoverCarouselNav>
                        </TeachingPopoverCarouselFooter>
                    </TeachingPopoverCarousel>
                </TeachingPopoverSurface>
            </TeachingPopover>
        );
    };

    return (
        <div className={ styles.shiftBlockContainer }
            ref={containerRef}
            aria-label={shiftAriaLabel}
            tabIndex={isInteractiveMode ? -1 : 0}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleContainerFocus}
            onBlur={handleBlur}
            onClick={handleContainerClick}
            onKeyDown={handleContainerKeyDown}>
            <div className={ styles.shiftBlockContent } >
                <ShiftThemeContainer
                    theme={ shift.theme }
                    nonClickable={ true }
                    className={ styles.shiftThemeContainer }>
                            <Shift
                                shift={ shift } showUnsharedStatus={ false } scheduleCellRenderSize={ ScheduleCellRenderSize.Large } doShowIcons={ false } doShowNotes={ false } doShow24HrShift={ false } showGroupName={ false }/>
                </ShiftThemeContainer>
                <Tooltip content={_smartScheduleStrings.get("slotsTooltip")} relationship="label">
                    <span style={{ display: 'inline-block' }}>
                        <SlotSelector
                            tabIndex={isInteractiveMode ? 0 : -1}
                            className={styles.slotDropdown}
                            appearance="filled-darker"
                            options={options}
                            selectedSlots={shift.openSlots}
                            onSelectionChange={_onSmartScheduleSlotChange}
                            ariaLabel={getStrings("addEditShiftTimeOffPanelFullPage", "slotsFieldAriaLabel")}
                            automationId={AutomationUtils.getAutomationId("addEditShiftPanel", "QAIDOpenShiftSlots")}
                        />
                    </span>
                </Tooltip>
                <div ref={repeatEverydayCheckboxRef}>
                    <Checkbox
                        tabIndex={isInteractiveMode ? 0 : -1}
                        label={ <Text className={styles.shiftsBlockLabel}>{getStrings("smartSchedule", "repeatEveryday")}</Text> }
                        checked={ !showWeekdays }
                        onChange={ onChangeRepeatEveryDay }
                        data-automation-id={ AutomationUtils.getAutomationId("addEditShiftPanel", "QAIDRepeatOnCheckbox") }
                    />
                </div>
                {
                    shouldShowIconButtons &&
                        <div className={ styles.iconButtonsContainer } >
                            <Tooltip content={getStrings("smartSchedule", "deleteShiftBlock")} relationship="label">
                                <Button
                                    tabIndex={isInteractiveMode ? 0 : -1}
                                    appearance="outline"
                                    icon={<DeleteRegular/>}
                                    onClick={() => {
                                        deleteShiftBlock(shift.id);
                                        props.onDeleted?.();
                                    }}
                                    />
                            </Tooltip>
                            <Tooltip content={getStrings("smartSchedule", "editShiftBlock")} relationship="label">
                                <Button
                                    ref={editButtonRef}
                                    tabIndex={isInteractiveMode ? 0 : -1}
                                    appearance="outline"
                                    icon={<EditRegular />}
                                    onClick={() => onEditShiftBlock(shift.id)}/>
                            </Tooltip>
                        </div>
                }
            </div>
            {showWeekdays && <Text className={styles.shiftsBlockLabel}>{getStrings("smartSchedule", "repeatOn")}</Text>}
            {showWeekdays &&
                <div
                    className={ styles.repeatOnContainer }
                    role="toolbar"
                    aria-label={getStrings("smartSchedule", "repeatOn")}
                    aria-orientation="horizontal">
                    {Array.from(WEEKDAYS_MAP).map(([key, day], index) => {
                        const isSelected = selectedDays.includes(day);
                        const isCurrentlyFocused = focusedDayIndex === index;
                        // Only make weekday keyboard-accessible when in interactive mode
                        const weekdayTabIndex = isInteractiveMode && isCurrentlyFocused ? 0 : -1;
                        const fullDayName = WEEKDAYS_FULL_NAME_MAP.get(day) || key;
                        return (
                            <div
                                key={key}
                                ref={(el) => (weekdayRefs.current[index] = el)}
                                className={classNames(styles.dayOfTheWeek, isSelected ? styles.selected : styles.unselected)}
                                role="button"
                                tabIndex={weekdayTabIndex}
                                aria-pressed={isSelected}
                                aria-label={isSelected
                                    ? getStrings("smartSchedule", "weekdaySelectedAriaLabel", [fullDayName])
                                    : getStrings("smartSchedule", "weekdayNotSelectedAriaLabel", [fullDayName])}
                                onClick={() => handleWeekdayClick(day, index)}
                                onKeyDown={(e) => handleWeekdayKeyDown(e, index, day)}>
                                {key}
                            </div>
                        );
                    })}
                </div>
            }
            {renderShiftBlocksTeachingPopover()}
        </div>
    );
};

export default observer(ShiftBlock);
